import React from 'react';
import { Download, Star, User, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MiniProduct = () => {
  const [showForm, setShowForm] = React.useState(false);
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  // Check URL hash on component mount to open form automatically
  React.useEffect(() => {
    if (window.location.hash === '#pdf-guide') {
      setShowForm(true);
      // Scroll to this section
      document.getElementById('pdf-guide')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send contact info to backend via the unified interaction endpoint
      await axios.post(`${API}/interaction`, {
        ...formData,
        serviceType: 'pdf_guide'
      });
      
      // Close form and show success message
      setShowForm(false);
      alert('Vielen Dank für deine Anfrage! Du erhältst den PDF-Guide in Kürze per E-Mail an: ' + formData.email);
      setFormData({ firstName: '', lastName: '', phone: '', email: '' });
    } catch (error) {
      console.error('Error submitting PDF guide request:', error);
      alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    }
  };

  return (
    <section id="pdf-guide" style={{
      padding: 'var(--spacing-giant) 0',
      background: 'var(--bg-section)'
    }}>
      <div className="container">
        <div className="network-card" style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
          cursor: 'default'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 'var(--spacing-medium)'
          }}>
            <div style={{
              background: 'var(--brand-primary)',
              padding: 'var(--spacing-medium)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Download size={48} style={{ color: 'var(--brand-dark)' }} />
            </div>
          </div>

          <h2 className="heading-2" style={{ marginBottom: 'var(--spacing-small)' }}>
            PDF-Guide: "5 Gewohnheiten, die dein Leben verbessern"
          </h2>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: 'var(--spacing-medium)' }}>
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} fill="var(--brand-primary)" stroke="var(--brand-primary)" />
            ))}
          </div>

          <p className="body-large" style={{ marginBottom: 'var(--spacing-medium)' }}>
            Einfache, wissenschaftlich fundierte Gewohnheiten, die deine Gesundheit, Energie und Lebensqualität nachhaltig verbessern.
          </p>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-medium)',
            marginBottom: 'var(--spacing-large)'
          }}>
            <span style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 700, color: 'var(--brand-dark)' }}>
              Kostenlos
            </span>
            <span style={{ fontSize: '1rem', color: 'var(--text-light)', textDecoration: 'line-through' }}>
              statt 9,99€
            </span>
          </div>

          <button className="btn-cta" onClick={() => setShowForm(true)}>
            Jetzt kostenlos herunterladen
          </button>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px',
          overflowY: 'auto'
        }} onClick={() => setShowForm(false)}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '32px',
            padding: 'var(--spacing-xl)',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 69, 52, 0.3)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-2" style={{ marginBottom: 'var(--spacing-small)', textAlign: 'center' }}>
              PDF-Guide herunterladen
            </h3>
            <p className="body-medium" style={{ marginBottom: 'var(--spacing-large)', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Fülle das Formular aus, um den kostenlosen Guide zu erhalten.
            </p>
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 'var(--spacing-medium)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Vorname *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border 0.2s ease'
                    }}
                    placeholder="Dein Vorname"
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--spacing-medium)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Nachname *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                    placeholder="Dein Nachname"
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--spacing-medium)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Telefonnummer (optional)
                </label>
                <div style={{ position: 'relative' }}>
                  <Phone size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                    placeholder="Deine Telefonnummer"
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--spacing-large)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  E-Mail-Adresse *
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 48px',
                      borderRadius: '20px',
                      border: '2px solid var(--border-medium)',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                    placeholder="deine@email.de"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--spacing-medium)' }}>
                <button 
                  type="submit"
                  className="btn-primary"
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Jetzt herunterladen
                </button>
                <button 
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};